import { useRef } from 'react'

/**
 * ASCAP logo: dark mode turns #231F20 lettering white; keeps blue gradient “A” and #2DA7F9 small caps.
 *
 * Wrapped in a fixed-size box + svg fillParent — avoids iOS Safari collapsing inline SVGs
 * in flex cells when only max-* / w-auto sizing is used (logos missing on mobile production).
 */
export default function AscapMark({ className = '' }: { className?: string }) {
  const gradIdRef = useRef<string | null>(null)
  if (gradIdRef.current === null) {
    gradIdRef.current =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? `ascap-grad-${crypto.randomUUID()}`
        : `ascap-grad-${Math.random().toString(36).slice(2, 12)}`
  }
  const gradId = gradIdRef.current

  return (
    <span
      className={`inline-flex h-8 w-[4.5rem] shrink-0 items-center justify-center overflow-visible dark:drop-shadow-[0_0.5px_0_rgba(0,0,0,0.5)] ${className}`}
    >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 294.8 105.7"
      fill="none"
      className="block h-full w-full max-h-8 max-w-full"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
      focusable="false"
    >
      <defs>
        <linearGradient
          id={gradId}
          gradientUnits="userSpaceOnUse"
          x1="-14.6388"
          y1="23.6991"
          x2="38.1975"
          y2="76.5353"
          gradientTransform="matrix(1 0 0 -1 0 105.8898)"
        >
          <stop offset="0" stopColor="#1498E2" />
          <stop offset="1" stopColor="#2DA7F9" />
        </linearGradient>
      </defs>
      <g>
        <path
          className="fill-[#231F20] dark:fill-white"
          d="M63.8,70.9v-0.2c0-10.4,7.9-15.2,19.2-15.2c4.8,0,8.3,0.8,11.6,2v-0.8c0-5.6-3.5-8.7-10.2-8.7c-5.2,0-8.8,1-13.2,2.6l-3.4-10.3c5.2-2.3,10.4-3.8,18.5-3.8c7.4,0,12.7,2,16.1,5.3c3.6,3.6,5.2,8.8,5.2,15.2v27.6h-13v-5.2c-3.3,3.6-7.8,6-14.4,6C71.2,85.5,63.8,80.3,63.8,70.9z M94.8,67.8v-2.4c-2.3-1.1-5.3-1.8-8.6-1.8c-5.8,0-9.3,2.3-9.3,6.6v0.2c0,3.6,3,5.8,7.4,5.8C90.5,76.1,94.8,72.7,94.8,67.8z"
        />
        <path
          className="fill-[#231F20] dark:fill-white"
          d="M109.8,78.3l5.8-8.9c5.2,3.7,10.6,5.7,15,5.7c3.9,0,5.7-1.4,5.7-3.6v-0.2c0-2.9-4.6-3.9-9.9-5.5c-6.7-2-14.2-5.1-14.2-14.3v-0.2c0-9.7,7.8-15.1,17.4-15.1c6,0,12.6,2,17.8,5.5l-5.2,9.3c-4.7-2.8-9.4-4.4-12.9-4.4c-3.3,0-5,1.4-5,3.3v0.2c0,2.7,4.5,3.9,9.7,5.7c6.7,2.2,14.4,5.4,14.4,14.1v0.2c0,10.6-7.9,15.4-18.2,15.4C123.5,85.5,116.1,83.2,109.8,78.3z"
        />
        <path
          className="fill-[#231F20] dark:fill-white"
          d="M150,61v-0.2c0-13.6,10.4-24.8,25-24.8c9,0,14.6,3,19,8l-8.3,8.9c-3-3.2-6-5.2-10.8-5.2c-6.8,0-11.6,6-11.6,13v0.2c0,7.3,4.7,13.2,12.1,13.2c4.5,0,7.6-2,10.9-5.1l7.9,8c-4.6,5.1-10,8.7-19.5,8.7C160.5,85.6,150,74.6,150,61z"
        />
        <path
          className="fill-[#231F20] dark:fill-white"
          d="M195.5,70.9v-0.2c0-10.4,7.9-15.2,19.2-15.2c4.8,0,8.3,0.8,11.6,2v-0.8c0-5.6-3.5-8.7-10.2-8.7c-5.2,0-8.8,1-13.2,2.6l-3.4-10.3c5.2-2.3,10.4-3.8,18.5-3.8c7.4,0,12.7,2,16.1,5.3c3.6,3.6,5.2,8.8,5.2,15.2v27.6h-13.1v-5.2c-3.3,3.6-7.8,6-14.4,6C202.9,85.5,195.5,80.3,195.5,70.9z M226.6,67.8v-2.4c-2.3-1.1-5.3-1.8-8.6-1.8c-5.8,0-9.3,2.3-9.3,6.6v0.2c0,3.6,3,5.8,7.4,5.8C222.3,76.1,226.6,72.7,226.6,67.8z"
        />
        <path
          className="fill-[#231F20] dark:fill-white"
          d="M244.7,36.9h13.5v6.8c3.3-4.4,7.8-7.7,14.8-7.7c11.1,0,21.7,8.7,21.7,24.6v0.2c0,15.9-10.4,24.6-21.7,24.6c-7.2,0-11.6-3.3-14.8-7.1v20.4h-13.5L244.7,36.9L244.7,36.9z M281.3,60.8v-0.2c0-7.9-5.3-13.2-11.6-13.2c-6.3,0-11.6,5.2-11.6,13.2v0.2c0,7.9,5.2,13.2,11.6,13.2C275.9,74,281.3,68.8,281.3,60.8z"
        />
      </g>
      <path
        fill={`url(#${gradId})`}
        d="M1.3,0h6.6c0,0,2.4,8.6,10.1,14.6c0,0,6.1,5,17.1,12.8c0,0,16,12.3,19.1,22.4c0,0,2.7,10.5-6.4,29.6h-4c0,0,7.1-12.6,5.9-22.4c0,0-0.1-9.1-13.8-19.8c0,0-10.6-7.4-18.8-8.2l18.6,57.5c0,0,5,15.3-11.6,18.8c0,0-20.2,4.4-24-11.6c0,0-2.1-10.8,10.7-14.7c0,0,5.6-1.8,15.5-0.4L1.3,0z"
      />
      <g fill="#2DA7F9">
        <path d="M78.1,93.7h2.3l-3.2,11.7h-2.4L72.1,97l-2.7,8.4H67l-3.3-11.7h2.4l2.2,8.1l2.6-8.1h2.4l2.6,8.1L78.1,93.7z" />
        <path d="M83.6,105.5V93.7H91v2.2h-5.1v2.7h4.6v2h-4.6v2.7H91v2.2L83.6,105.5L83.6,105.5z" />
        <path d="M99.9,99.6c0-3.7,2.8-6.1,6-6.1s4.9,2,5.4,3.9l-2.2,0.7c-0.3-1.1-1.2-2.4-3.2-2.4c-1.8,0-3.6,1.3-3.6,3.9c0,2.5,1.8,3.9,3.6,3.9c2,0,3-1.3,3.3-2.4l2.1,0.6c-0.4,1.8-2.1,4-5.4,4C102.7,105.7,99.9,103.3,99.9,99.6z" />
        <path d="M118.4,100.9h-1.2v4.5h-2.3V93.7h4.6c2.3,0,3.7,1.6,3.7,3.6c0,1.6-1,2.9-2.5,3.3l2.5,4.8h-2.6L118.4,100.9z M119.1,99c1.2,0,1.8-0.7,1.8-1.6c0-1-0.7-1.6-1.8-1.6h-1.9V99H119.1z" />
        <path d="M127,105.5V93.7h7.4v2.2h-5.1v2.7h4.6v2h-4.6v2.7h5.1v2.2L127,105.5L127,105.5z" />
        <path d="M145.2,102.8h-4.8l-1,2.7H137l4.5-11.7h2.7l4.5,11.7h-2.5L145.2,102.8z M141.2,100.7h3.2l-1.6-4.4L141.2,100.7z" />
        <path d="M155.7,95.9v9.6h-2.3v-9.6h-3.7v-2.2h9.7v2.2H155.7z" />
        <path d="M162.7,105.5V93.7h7.4v2.2H165v2.7h4.6v2H165v2.7h5.1v2.2L162.7,105.5L162.7,105.5z" />
        <path d="M190.6,105.5v-8.2l-3.4,8.2h-1.9l-3.4-8.2v8.2h-2.2V93.7h3l3.5,8.5l3.4-8.5h3.1v11.7h-2.1V105.5z" />
        <path d="M197.1,101.3v-7.6h2.3v7.4c0,1.5,0.9,2.4,2.2,2.4c1.4,0,2.2-0.8,2.2-2.4v-7.4h2.3v7.6c0,2.9-2,4.4-4.5,4.4C199.1,105.7,197.1,104.2,197.1,101.3z" />
        <path d="M216.2,97.1c-0.1-0.6-0.6-1.7-2.1-1.7c-1.1,0-1.8,0.7-1.8,1.5c0,0.6,0.4,1.2,1.3,1.3l1.6,0.3c2.1,0.4,3.3,1.8,3.3,3.5c0,1.8-1.5,3.6-4.2,3.6c-3.1,0-4.5-2-4.7-3.7l2.1-0.6c0.1,1.2,0.9,2.2,2.6,2.2c1.2,0,1.9-0.6,1.9-1.4c0-0.7-0.5-1.2-1.4-1.4l-1.6-0.3c-1.9-0.4-3.1-1.6-3.1-3.4c0-2.1,1.9-3.7,4.1-3.7c2.8,0,3.9,1.7,4.2,3L216.2,97.1z" />
        <path d="M222,105.5V93.7h2.3v11.7H222V105.5z" />
        <path d="M227.9,99.6c0-3.7,2.8-6.1,6-6.1s4.9,2,5.4,3.9l-2.2,0.7c-0.3-1.1-1.2-2.4-3.2-2.4c-1.8,0-3.6,1.3-3.6,3.9c0,2.5,1.8,3.9,3.6,3.9c2,0,3-1.3,3.3-2.4l2.1,0.6c-0.4,1.8-2.1,4-5.4,4C230.7,105.7,227.9,103.3,227.9,99.6z" />
      </g>
    </svg>
    </span>
  )
}
